import React, { useState } from 'react';

import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	Box,
	Group,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import './FilterSection.scss';

export interface FilterOption {
	value: string;
	label: string;
	count?: number;
}

interface FilterSectionProps {
	/** The title to display in the section header */
	title: string;
	/** Whether the section should be wrapped in an accordion (default: true) */
	insideAccordion?: boolean;
	/** The content to display in the section body */
	children?: React.ReactNode;
	/** Optional component to render next to the title (only when not in accordion) */
	titleComponent?: React.ReactNode;
	/** Whether this is the first section (removes top padding) */
	isFirst?: boolean;
	/** Whether the accordion should be disabled (e.g., while loading) */
	isDisabled?: boolean;
	/** Whether the accordion should start expanded */
	defaultExpanded?: boolean;
}

/**
 * FilterSection component that provides a consistent structure for filter sections
 * with optional accordion wrapper and standardized styling
 */
export function FilterSection({
	title,
	insideAccordion = true,
	children,
	titleComponent,
	isFirst = false,
	isDisabled = false,
	defaultExpanded = false,
}: FilterSectionProps) {
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	const titleText = (
		<Text size='body' weight='semibold' align='left'>
			{title}
		</Text>
	);
	const header = titleComponent ? (
		<Group>
			{titleText}
			{titleComponent}
		</Group>
	) : (
		titleText
	);

	const content = children ? (
		<Box padding={{ top: 12, bottom: insideAccordion ? 12 : 0 }}>{children}</Box>
	) : null;

	return (
		<>
			{insideAccordion ? (
				<Accordion
					isExpanded={isExpanded}
					onExpandedChange={setIsExpanded}
					isDisabled={isDisabled}
					className='filter-section-accordion'>
					<AccordionHeader>
						<Box
							padding={{
								top: isFirst ? 0 : 20,
								bottom: isExpanded ? 12 : 20,
							}}>
							{titleText}
						</Box>
					</AccordionHeader>
					<AccordionBody>{content}</AccordionBody>
				</Accordion>
			) : (
				<Stack padding={isFirst ? { bottom: 20 } : { y: 20 }}>
					{header}
					{content}
				</Stack>
			)}
		</>
	);
}
